<template>
  <div class="currency-exchange-page">
    <section v-if="!loggedIn" class="exchange-login">
      <span>ASSET EXCHANGE</span>
      <h1>小麦转换披萨币</h1>
      <p>登录后可查看小麦余额、实时转换比例和转换记录。</p>
      <button type="button" @click="openLogin">登录 / 注册</button>
    </section>

    <template v-else>
      <section class="exchange-hero">
        <div class="exchange-heading">
          <span>ASSET EXCHANGE</span>
          <h1>小麦转换披萨币</h1>
          <p>饰品回收后获得的小麦可在这里转换为披萨币。转换成功后立即到账，记录不可撤销。</p>
        </div>
        <div class="rate-card" :class="{ disabled: !config.enabled }">
          <span>当前转换比例</span>
          <strong>1 <i class="wheat-icon"></i></strong>
          <em>=</em>
          <strong>{{ rateText }} <i class="pizza-icon"></i></strong>
          <small>{{ config.enabled ? "通道开放中" : "通道暂未开放" }}</small>
        </div>
      </section>

      <section class="exchange-panel">
        <div class="balance-flow">
          <article class="balance-card wheat">
            <span>转换前</span>
            <div><i></i><strong>{{ moneyText(masonry) }}</strong></div>
            <em>小麦余额</em>
          </article>
          <div class="flow-arrow"><i></i><span>单向转换</span></div>
          <article class="balance-card pizza">
            <span>转换后</span>
            <div><i></i><strong>{{ moneyText(projectedPizzaBalance) }}</strong></div>
            <em>披萨币余额</em>
          </article>
        </div>

        <div class="exchange-form">
          <div class="form-title">
            <div><span>输入数量</span><strong>本次转换</strong></div>
            <em>单笔 {{ moneyText(config.min_amount) }} - {{ moneyText(config.max_amount) }}</em>
          </div>
          <div class="amount-field">
            <i class="wheat-icon"></i>
            <input v-model="amount" type="number" step="0.01" :min="config.min_amount" :max="config.max_amount" placeholder="0.00" />
            <button type="button" @click="setAmount(100)">MAX</button>
          </div>
          <div class="quick-amounts">
            <button v-for="percent in [25, 50, 75, 100]" :key="percent" type="button" @click="setAmount(percent)">{{ percent }}%</button>
          </div>
          <div class="receive-row">
            <span>预计获得</span>
            <strong>{{ moneyText(receiveAmount) }} <i class="pizza-icon"></i></strong>
          </div>
          <button class="submit-exchange" type="button" :disabled="submitting || !config.enabled" @click="submitExchange">
            {{ submitting ? "转换处理中" : config.enabled ? "确认转换" : "通道暂未开放" }}
          </button>
          <p>披萨币用于打开箱子及站内玩法；小麦转换为披萨币后不能反向转换。</p>
        </div>
      </section>

      <section class="exchange-history">
        <header>
          <div><span>EXCHANGE HISTORY</span><h2>最近转换记录</h2></div>
          <button type="button" @click="loadInfo">刷新</button>
        </header>
        <div v-if="records.length" class="history-list">
          <article v-for="record in records" :key="record.id">
            <div><span>{{ record.order_no }}</span><em>{{ record.create_time }}</em></div>
            <strong><i class="wheat-icon"></i>{{ moneyText(record.wheat_amount) }}</strong>
            <b>1 : {{ Number(record.exchange_rate).toFixed(4) }}</b>
            <strong class="pizza"><i class="pizza-icon"></i>{{ moneyText(record.pizza_amount) }}</strong>
          </article>
        </div>
        <div v-else class="history-empty">暂无转换记录</div>
      </section>
    </template>
  </div>
</template>

<script>
import Utils from "./../assets/js/util.js";

export default {
  name: "CurrencyExchange",
  data() {
    return {
      amount: "",
      masonry: 0,
      pizzaBalance: 0,
      config: {
        enabled: 1,
        rate: 1,
        min_amount: 1,
        max_amount: 100000,
      },
      records: [],
      submitting: false,
    };
  },
  computed: {
    loggedIn() {
      return Boolean(this.$store.state.token || localStorage.getItem("token"));
    },
    receiveAmount() {
      return Math.max(0, Number(this.amount || 0) * Number(this.config.rate || 0));
    },
    projectedPizzaBalance() {
      return Number(this.pizzaBalance || 0) + this.receiveAmount;
    },
    rateText() {
      return Number(this.config.rate || 0).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
    },
  },
  watch: {
    loggedIn(value) {
      if (value) this.loadInfo();
    },
  },
  mounted() {
    if (this.loggedIn) this.loadInfo();
  },
  methods: {
    openLogin() {
      this.$store.commit("getLogin", true);
    },
    moneyText(value) {
      return Number(value || 0).toFixed(2);
    },
    loadInfo() {
      this.$axios.post("/index/Currency_exchange/info", this.$qs.stringify({})).then((res) => {
        if (res.data.status != 1) {
          this.$message.error(res.data.msg || "读取转换信息失败");
          return;
        }
        const data = res.data.data;
        this.masonry = Number(data.masonry || 0);
        this.pizzaBalance = Number(data.total_amount || 0);
        this.config = data.config;
        this.records = data.records || [];
      });
    },
    setAmount(percent) {
      const value = Math.min(Number(this.masonry || 0), Number(this.config.max_amount || 0)) * percent / 100;
      this.amount = value.toFixed(2);
    },
    submitExchange() {
      const amount = Number(this.amount || 0);
      if (amount < Number(this.config.min_amount) || amount > Number(this.config.max_amount)) {
        this.$message.error(`单笔转换范围为 ${this.moneyText(this.config.min_amount)} - ${this.moneyText(this.config.max_amount)} 小麦`);
        return;
      }
      if (amount > Number(this.masonry)) {
        this.$message.error("小麦余额不足");
        return;
      }
      this.$confirm(`确认将 ${amount.toFixed(2)} 小麦转换为 ${this.moneyText(this.receiveAmount)} 披萨币？`, "确认转换", {
        confirmButtonText: "确认转换",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        this.submitting = true;
        this.$axios.post("/index/Currency_exchange/exchange", this.$qs.stringify({ amount: amount.toFixed(2) })).then((res) => {
          if (res.data.status != 1) {
            this.$message.error(res.data.msg || "转换失败");
            return;
          }
          const data = res.data.data;
          this.masonry = Number(data.masonry);
          this.pizzaBalance = Number(data.total_amount);
          this.$store.commit("getMasonry", data.masonry);
          this.$store.commit("getMoney", data.total_amount);
          Utils.$emit("masonry", data.masonry);
          Utils.$emit("money", data.total_amount);
          const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
          userInfo.masonry = data.masonry;
          userInfo.total_amount = data.total_amount;
          localStorage.setItem("userInfo", JSON.stringify(userInfo));
          this.amount = "";
          this.$message.success("转换成功，披萨币已到账");
          this.loadInfo();
        }).finally(() => {
          this.submitting = false;
        });
      });
    },
  },
};
</script>

<style scoped>
.currency-exchange-page {
  min-height: calc(100vh - 270px);
  padding: 36px 34px 90px;
  color: #f5f0e5;
  background:
    radial-gradient(circle at 48% 6%, rgba(244, 190, 73, 0.12), transparent 30%),
    linear-gradient(180deg, #20222e 0%, #171923 100%);
}

button,
input { font-family: inherit; }

.exchange-hero,
.exchange-panel,
.exchange-history,
.exchange-login {
  width: 100%;
  max-width: 1450px;
  margin: 0 auto;
}

.exchange-hero {
  min-height: 260px;
  padding: 42px 48px;
  display: grid;
  grid-template-columns: 1fr 360px;
  align-items: center;
  gap: 42px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(247, 199, 84, 0.24);
  border-radius: 16px;
  background:
    radial-gradient(circle at 80% 50%, rgba(247, 199, 84, 0.2), transparent 32%),
    linear-gradient(135deg, #2b292a 0%, #242630 56%, #1d1f2a 100%);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
}

.exchange-heading span,
.exchange-history header span,
.exchange-login > span {
  color: #f0a82c;
  font-size: 12px;
  font-weight: 1000;
  letter-spacing: 0.18em;
}

.exchange-heading h1,
.exchange-login h1 {
  margin: 12px 0 16px;
  color: #fff;
  font-size: clamp(38px, 4vw, 66px);
  line-height: 1;
  font-weight: 1000;
}

.exchange-heading p,
.exchange-login p {
  max-width: 690px;
  margin: 0;
  color: #b7b8c4;
  font-size: 15px;
  line-height: 1.8;
}

.rate-card {
  min-height: 174px;
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(255, 210, 103, 0.24);
  border-radius: 14px;
  background: rgba(21, 22, 29, 0.7);
}

.rate-card > span,
.rate-card > small { grid-column: 1 / -1; text-align: center; }
.rate-card > span { color: #aeb1bd; font-size: 13px; font-weight: 900; }
.rate-card strong { display: flex; align-items: center; justify-content: center; gap: 8px; color: #ffe19a; font-size: 25px; }
.rate-card em { color: #747887; font-size: 20px; font-style: normal; }
.rate-card small { color: #61dba8; font-weight: 900; }
.rate-card.disabled small { color: #ff7474; }

.wheat-icon,
.pizza-icon { width: 22px; height: 22px; display: inline-block; background: center / contain no-repeat; }
.wheat-icon { background-image: url("../assets/pizzaking/wheat-token.svg"); }
.pizza-icon { background-image: url("../assets/pizzaking/pizza-token.svg"); }

.exchange-panel {
  margin-top: 18px;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(380px, 0.8fr);
  gap: 18px;
}

.balance-flow,
.exchange-form,
.exchange-history {
  border: 1px solid #363947;
  border-radius: 14px;
  background: #222430;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.2);
}

.balance-flow {
  min-height: 360px;
  padding: 36px;
  display: grid;
  grid-template-columns: 1fr 120px 1fr;
  align-items: center;
  gap: 12px;
}

.balance-card { min-height: 210px; padding: 28px; display: flex; flex-direction: column; justify-content: center; border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; background: #292c39; }
.balance-card > span { color: #8f94a4; font-size: 12px; font-weight: 900; letter-spacing: .12em; }
.balance-card > div { margin: 18px 0 8px; display: flex; align-items: center; gap: 12px; }
.balance-card > div i { width: 42px; height: 42px; display: block; background: center / contain no-repeat; }
.balance-card.wheat > div i { background-image: url("../assets/pizzaking/wheat-token.svg"); }
.balance-card.pizza > div i { background-image: url("../assets/pizzaking/pizza-token.svg"); }
.balance-card strong { min-width: 0; overflow: hidden; color: #fff0b3; font-size: clamp(28px, 3vw, 46px); text-overflow: ellipsis; }
.balance-card > em { color: #aeb2c0; font-size: 13px; font-style: normal; font-weight: 900; }

.flow-arrow { display: flex; flex-direction: column; align-items: center; gap: 14px; color: #8d91a0; font-size: 11px; font-weight: 900; }
.flow-arrow i { width: 72px; height: 2px; position: relative; background: linear-gradient(90deg, rgba(244,190,73,.1), #f4be49); box-shadow: 0 0 18px rgba(244,190,73,.32); }
.flow-arrow i::after { content: ""; position: absolute; right: -1px; top: -5px; border-left: 9px solid #f4be49; border-top: 6px solid transparent; border-bottom: 6px solid transparent; }

.exchange-form { padding: 30px; }
.form-title { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; }
.form-title div { display: flex; flex-direction: column; gap: 5px; }
.form-title span { color: #8f94a4; font-size: 12px; font-weight: 900; }
.form-title strong { color: #fff; font-size: 23px; }
.form-title em { color: #8f94a4; font-size: 12px; font-style: normal; }

.amount-field { height: 66px; margin-top: 24px; padding: 0 12px 0 18px; display: flex; align-items: center; gap: 12px; border: 1px solid #494d5e; border-radius: 9px; background: #1b1d27; }
.amount-field input { min-width: 0; flex: 1; color: #fff4d0; font-size: 28px; font-weight: 1000; outline: 0; border: 0; background: transparent; }
.amount-field button { height: 36px; padding: 0 14px; color: #ffd66f; font-weight: 1000; border: 1px solid rgba(255,214,111,.25); border-radius: 6px; background: rgba(255,214,111,.08); cursor: pointer; }
.quick-amounts { margin-top: 10px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.quick-amounts button { height: 34px; color: #b7bbc8; font-weight: 900; border: 1px solid #3b3e4c; border-radius: 6px; background: #292c38; cursor: pointer; }
.quick-amounts button:hover { color: #ffd66f; border-color: rgba(255,214,111,.35); }

.receive-row { margin: 23px 0; padding: 18px 0; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #363947; border-bottom: 1px solid #363947; }
.receive-row span { color: #9ba0af; font-size: 13px; font-weight: 900; }
.receive-row strong { display: flex; align-items: center; gap: 7px; color: #ffd66f; font-size: 22px; }
.submit-exchange { width: 100%; height: 54px; color: #241a08; font-size: 17px; font-weight: 1000; border: 0; border-radius: 8px; background: linear-gradient(180deg, #ffd66f, #f49a22); box-shadow: 0 12px 28px rgba(244,154,34,.23); cursor: pointer; }
.submit-exchange:disabled { color: #777b89; background: #343744; box-shadow: none; cursor: not-allowed; }
.exchange-form > p { margin: 15px 0 0; color: #777c8d; font-size: 12px; line-height: 1.6; }

.exchange-history { margin-top: 18px; padding: 28px; }
.exchange-history header { display: flex; align-items: center; justify-content: space-between; }
.exchange-history h2 { margin: 4px 0 0; color: #fff; font-size: 24px; }
.exchange-history header button { height: 36px; padding: 0 16px; color: #bdc1cd; font-weight: 900; border: 1px solid #414553; border-radius: 6px; background: #292c38; cursor: pointer; }
.history-list { margin-top: 18px; }
.history-list article { min-height: 66px; padding: 11px 16px; display: grid; grid-template-columns: minmax(240px, 1fr) 170px 130px 170px; align-items: center; gap: 16px; border-top: 1px solid #343744; }
.history-list article > div { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.history-list article > div span { overflow: hidden; color: #d9dce5; font-size: 12px; font-weight: 900; text-overflow: ellipsis; }
.history-list article > div em { color: #777c8c; font-size: 11px; font-style: normal; }
.history-list article > strong { display: flex; align-items: center; gap: 8px; color: #f7d66c; }
.history-list article > strong.pizza { color: #ffae43; }
.history-list article > b { color: #858a99; font-size: 12px; }
.history-empty { margin-top: 18px; padding: 46px; color: #777c8c; text-align: center; border-top: 1px solid #343744; }

.exchange-login { min-height: 410px; padding: 70px; border: 1px solid rgba(247,199,84,.24); border-radius: 16px; background: radial-gradient(circle at 75% 50%, rgba(247,199,84,.18), transparent 30%), #232530; }
.exchange-login button { height: 48px; margin-top: 28px; padding: 0 28px; color: #241a08; font-weight: 1000; border: 0; border-radius: 7px; background: linear-gradient(180deg, #ffd66f, #f49a22); cursor: pointer; }

@media (max-width: 1100px) {
  .exchange-hero,
  .exchange-panel { grid-template-columns: 1fr; }
  .rate-card { max-width: 420px; }
  .history-list article { grid-template-columns: 1fr 130px 110px 130px; }
}

@media (max-width: 720px) {
  .currency-exchange-page { padding: 18px 12px 58px; }
  .exchange-hero,
  .exchange-login { padding: 28px 22px; }
  .balance-flow { padding: 18px; grid-template-columns: 1fr; }
  .flow-arrow { transform: rotate(90deg); margin: 8px 0; }
  .exchange-form,
  .exchange-history { padding: 20px 16px; }
  .form-title { align-items: flex-start; flex-direction: column; }
  .history-list article { padding: 15px 4px; grid-template-columns: 1fr 1fr; }
  .history-list article > b { text-align: right; }
}
</style>
